import { Box, Card, Divider, styled, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import InfoOutline from '@mui/icons-material/InfoOutline';
import { CurrencyCode } from 'further-types/lp/vehicle';
import FormField from 'further-ui/components/FormField';
import NumberInput from 'further-ui/components/NumberInput';
import { Column, Row } from 'further-ui/components/Layout';
import Switch from 'further-ui/components/Switch';
import useInvestorAndReviewSettings from './useInvestorAndReviewSettings';
import { ClassificationFlow } from 'further-types/lp/investor-classification';
import ClassificationCard from './ClassificationCard';
import BasicSelect from 'further-ui/components/BasicSelect';
import ReviewSettings from './ReviewSettings';
import ErrorBoundary from 'components/ErrorBoundary';
import useOnboardingSettings from 'hooks/lp/data/vehicle/useOnboardingSettings';
import { useLayoutContext } from 'contexts/LayoutContext';
import CkEditor from 'components/CkEditor';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const SettingsContainer = styled(Box)({
  width: '100%',
});

type Props = {
  vehicleId: string;
};

const InvestorAndReviewSettings: React.FC<Props> = ({ vehicleId }) => {
  const { vehicle } = useLayoutContext();
  const baseCurrency = vehicle?.baseCurrency ?? CurrencyCode.GBP;
  const { onboardingSettings } = useOnboardingSettings(vehicleId);
  const [canReadUsers] = useHasPermissions([RolePermission.ReadUsers]);

  const {
    handleRequestBankDetailsChange,
    handleMinimumCommitmentSizeChange,
    handleGeneralCommitmentInstructionsChange,
    handleDefaultInternationalInvestorClassificationChange,
    euFlowSettings,
    ukFlowSettings,
    usFlowSettings,
    availableFlows,
    isLoading,
  } = useInvestorAndReviewSettings(vehicleId);

  return (
    <Column spacing="lg" fullWidth>
      <FormField
        spacing="xs"
        label="Request bank details in investor details step"
        tooltip="Select whether only core investor details (name, address etc) are requested during onboarding or if investor bank details are also required."
      >
        <Switch
          checked={onboardingSettings.requestBankDetails}
          onChange={handleRequestBankDetailsChange}
        />
      </FormField>
      <FormField
        label="Minimum commitment size"
        tooltip="Enter a minimum commitment size. Investors will not be able to complete subscriptions until they have confirmed a subscription amount meeting the minimum level. If you have no minimum level, leave this blank."
      >
        <NumberInput
          fullWidth
          clearZeroValueOnFocus
          size="small"
          currency={baseCurrency}
          value={onboardingSettings.minimumCommitmentSize}
          onBlur={handleMinimumCommitmentSizeChange}
        />
      </FormField>
      <FormField
        label="General commitment instructions"
        tooltip="Enter any general information you wish to alert investors to at subscription. This may include your proposed drawdown schedule and timing. Leave blank if you do not want to provide instructions."
      >
        <CkEditor
          value={onboardingSettings.generalCommitmentInstructions}
          onBlur={handleGeneralCommitmentInstructionsChange}
          placeholder="Enter commitment instructions"
          extraLines={2}
        />
      </FormField>
      <Divider />
      <SettingsContainer>
        <Column spacing="sm" fullWidth>
          <Row spacing="xxs" centered>
            <Typography variant="h6">
              Available investor classifications
            </Typography>
            <Tooltip
              title="Select which system classifications you wish to make available for investors. The system will dynamically adapt classification flows depending on the investor’s country of registration / residence."
              placement="top-start"
            >
              <InfoOutline fontSize="inherit" />
            </Tooltip>
          </Row>
          <Card variant="outlined">
            <ClassificationCard
              vehicleId={vehicleId}
              title="UK"
              classificationFlow={ClassificationFlow.UK}
              flowSettings={ukFlowSettings}
            />
          </Card>

          <Card variant="outlined">
            <ClassificationCard
              vehicleId={vehicleId}
              title="MiFID"
              classificationFlow={ClassificationFlow.EU}
              flowSettings={euFlowSettings}
            />
          </Card>

          <Card variant="outlined">
            <ClassificationCard
              vehicleId={vehicleId}
              title="US"
              classificationFlow={ClassificationFlow.US}
              flowSettings={usFlowSettings}
            />
          </Card>
        </Column>
      </SettingsContainer>
      <Divider />
      <SettingsContainer>
        <FormField
          size="md"
          spacing="sm"
          label="Default international investor classification"
          tooltip="Select which preset investor classification sign-up flow should apply to investors not resident in the UK, EU, EEA or US."
        >
          <BasicSelect
            disabled={isLoading}
            fullWidth
            size="small"
            value={onboardingSettings.defaultInternationalClassificationFlow}
            onChange={handleDefaultInternationalInvestorClassificationChange}
            options={availableFlows}
          />
        </FormField>
      </SettingsContainer>
      {canReadUsers ? (
        <>
          <Divider />
          <SettingsContainer>
            <ErrorBoundary>
              <ReviewSettings vehicleId={vehicleId} />
            </ErrorBoundary>
          </SettingsContainer>
        </>
      ) : null}
    </Column>
  );
};

export default InvestorAndReviewSettings;
